<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { apps, formatting, useRegistry, useContext, useDialogs, useLocalization } from '@hantera/portal-app'
import { DropdownButton, DropdownItem, EnumsBar, TextInput } from '@hantera/design-system'
import AppSlotView from '@/AppSlotView.vue'
import ChannelCountryRow from './ChannelCountryRow.vue'
import { COUNTRY_CODES } from '@/constants/country-codes'

const t = useLocalization('settings.channels')
const context = useContext()
const registry = useRegistry()
const dialogs = useDialogs()
const { country } = formatting

const canWrite = computed(() => context.authorize('registry/channels:write'))

const props = defineProps<{
  channelKey: string
  data: Record<string, any>
}>()

const emit = defineEmits<{
  (e: 'saved'): void
  (e: 'deleted'): void
}>()

const SYSTEM_FIELDS = ['label', 'inventories', 'countries']

const label = ref<string | Record<string, string>>('')
const selectedInventories = ref<string[]>([])
const countries = ref<Record<string, Record<string, any>>>({})
const pendingAppFields = ref<Record<string, any>>({})
const removedAppFields = ref<Set<string>>(new Set())
const pendingCountryFields = ref<Record<string, Record<string, any>>>({})
const saving = ref(false)

const allInventories = ref<Array<{ key: string; label: string }>>([])

watch(() => [props.channelKey, props.data], () => {
  resetForm()
  loadInventories()
}, { immediate: true })

function resetForm() {
  label.value = props.data?.label ?? ''
  selectedInventories.value = [...(props.data?.inventories ?? [])]
  countries.value = JSON.parse(JSON.stringify(props.data?.countries ?? {}))
  pendingAppFields.value = {}
  removedAppFields.value = new Set()
  pendingCountryFields.value = {}
}

function resolveInventoryLabel(lbl: any): string {
  if (!lbl) return ''
  if (typeof lbl === 'string') return lbl
  return lbl['default'] ?? Object.values(lbl)[0] ?? ''
}

async function loadInventories() {
  try {
    const data = await registry.list('inventories', true)
    allInventories.value = Object.entries(data).map(([fullKey, value]) => {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value
      return {
        key: fullKey.split('/').pop()!,
        label: resolveInventoryLabel(parsed?.label)
      }
    }).sort((a, b) => a.key.localeCompare(b.key))
  } catch {
    allInventories.value = []
  }
}

const isDirty = computed(() => {
  const origLabel = props.data?.label ?? ''
  const origInv = props.data?.inventories ?? []
  const origCountries = props.data?.countries ?? {}

  if (JSON.stringify(label.value) !== JSON.stringify(origLabel)) return true
  if (JSON.stringify(selectedInventories.value.sort()) !== JSON.stringify([...origInv].sort())) return true
  if (JSON.stringify(countries.value) !== JSON.stringify(origCountries)) return true
  if (Object.keys(pendingAppFields.value).length > 0) return true
  if (removedAppFields.value.size > 0) return true
  if (Object.keys(pendingCountryFields.value).length > 0) return true
  return false
})

const inventoryEnumSets = computed(() => [{
  label: t('editor.inventories'),
  values: allInventories.value.map(inv => ({
    key: inv.key,
    label: inv.label || inv.key
  }))
}])

function addInventory(value: { key: string }) {
  if (!selectedInventories.value.includes(value.key)) {
    selectedInventories.value = [...selectedInventories.value, value.key]
  }
}

function removeInventory(value: { key: string }) {
  selectedInventories.value = selectedInventories.value.filter(k => k !== value.key)
}

const displayNames = computed(() => {
  const lang = document.documentElement.lang ?? navigator.language
  return new Intl.DisplayNames([lang], { type: 'region' })
})

const availableCountries = computed(() => {
  const existing = new Set(Object.keys(countries.value))
  return COUNTRY_CODES
    .filter(c => !existing.has(c))
    .map(code => ({
      code,
      name: displayNames.value.of(code) ?? code
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
})

const countryFilter = ref('')
const filteredAvailableCountries = computed(() => {
  const filter = countryFilter.value.toLowerCase()
  if (!filter) return availableCountries.value
  return availableCountries.value.filter(c =>
    c.name.toLowerCase().includes(filter) || c.code.toLowerCase().includes(filter)
  )
})

function addCountry(code: string) {
  countries.value = { ...countries.value, [code]: {} }
  countryFilter.value = ''
}

function removeCountry(code: string) {
  const { [code]: _, ...rest } = countries.value
  countries.value = rest
}

const slotData = computed(() => {
  const base = { ...(props.data ?? {}) }
  for (const [key, value] of Object.entries(pendingAppFields.value)) {
    base[key] = value
  }
  for (const key of removedAppFields.value) {
    delete base[key]
  }
  return base
})

function setAppField(field: string, value: any) {
  if (SYSTEM_FIELDS.includes(field)) return
  pendingAppFields.value[field] = value
  removedAppFields.value.delete(field)
}

function removeAppField(field: string) {
  if (SYSTEM_FIELDS.includes(field)) return
  delete pendingAppFields.value[field]
  removedAppFields.value.add(field)
}

function setCountryAppField(countryCode: string, field: string, value: any) {
  if (!pendingCountryFields.value[countryCode]) {
    pendingCountryFields.value[countryCode] = {}
  }
  pendingCountryFields.value[countryCode][field] = value
}

function removeCountryAppField(countryCode: string, field: string) {
  if (pendingCountryFields.value[countryCode]) {
    delete pendingCountryFields.value[countryCode][field]
  }
  if (countries.value[countryCode]) {
    const updated = { ...countries.value[countryCode] }
    delete updated[field]
    countries.value = { ...countries.value, [countryCode]: updated }
  }
}

function getCountryData(countryCode: string): Record<string, any> {
  return {
    ...(countries.value[countryCode] ?? {}),
    ...(pendingCountryFields.value[countryCode] ?? {})
  }
}

async function save() {
  if (!canWrite.value || saving.value) return

  saving.value = true
  try {
    const record: Record<string, any> = {}

    for (const [key, value] of Object.entries(props.data ?? {})) {
      if (!SYSTEM_FIELDS.includes(key)) {
        record[key] = value
      }
    }

    for (const [key, value] of Object.entries(pendingAppFields.value)) {
      record[key] = value
    }

    for (const key of removedAppFields.value) {
      delete record[key]
    }

    record.label = typeof label.value === 'string' ? label.value.trim() : label.value
    record.inventories = selectedInventories.value

    const mergedCountries: Record<string, Record<string, any>> = {}
    for (const [code, data] of Object.entries(countries.value)) {
      mergedCountries[code] = {
        ...data,
        ...(pendingCountryFields.value[code] ?? {})
      }
    }
    record.countries = mergedCountries

    await registry.set(`channels/${props.channelKey}`, record)
    pendingAppFields.value = {}
    removedAppFields.value = new Set()
    pendingCountryFields.value = {}
    emit('saved')
  } catch (e) {
    console.error('Failed to save channel:', e)
  } finally {
    saving.value = false
  }
}

async function deleteChannel() {
  const confirmed = await dialogs.confirm(
    t('editor.deleteConfirm').replace('{key}', props.channelKey),
    t('editor.deleteTitle'),
    t('editor.delete'),
    undefined,
    true
  )
  if (!confirmed) return

  try {
    await registry.clear(`channels/${props.channelKey}`)
    emit('deleted')
  } catch (e) {
    console.error('Failed to delete channel:', e)
  }
}

const sortedCountryCodes = computed(() => {
  return Object.keys(countries.value).sort((a, b) => {
    const nameA = displayNames.value.of(a) ?? a
    const nameB = displayNames.value.of(b) ?? b
    return nameA.localeCompare(nameB)
  })
})
</script>

<template>
  <div class="channel-editor neat-scroll">
    <h2>{{ channelKey }}</h2>

    <fieldset class="channel-editor__section">
      <div>
        <label>{{ t('editor.label') }}</label>
        <LocalizedLabelInput v-model="label" :disabled="!canWrite" />
      </div>

      <div>
        <label>{{ t('editor.inventories') }}</label>
        <p class="field-help">{{ t('editor.inventoriesHelp') }}</p>

        <div v-if="allInventories.length === 0" class="sidebar-empty">
          {{ t('editor.noInventories') }}
        </div>
        <EnumsBar
          v-else
          :selected-values="selectedInventories"
          :available-sets="inventoryEnumSets"
          :disabled="!canWrite"
          @add="addInventory"
          @remove="removeInventory"
        />
      </div>
    
    </fieldset>

    <div class="channel-editor__section">
      <label>{{ t('editor.countries') }}</label>

      <div v-if="sortedCountryCodes.length === 0" class="sidebar-empty">
        {{ t('editor.noCountries') }}
      </div>

      <div v-else class="countries-list box-content">
        <ChannelCountryRow
          v-for="code in sortedCountryCodes"
          :key="code"
          :channel-key="channelKey"
          :country-code="code"
          :data="getCountryData(code)"
          :can-write="canWrite"
          @remove="removeCountry"
          @set-field="setCountryAppField"
          @remove-field="removeCountryAppField"
        />
      </div>

      <div class="add-country" v-if="canWrite">
        <DropdownButton :label="t('editor.addCountry')" class="add-country-btn">
          <div class="add-country-search">
            <TextInput v-model="countryFilter" placeholder="Filter..." />
          </div>
          <div class="add-country-list">
            <DropdownItem
              v-for="c in filteredAvailableCountries"
              :key="c.code"
              @click="addCountry(c.code)"
            >
              <span :class="`fi fi-${c.code.toLowerCase()}`"></span> {{ c.name }}
            </DropdownItem>
          </div>
        </DropdownButton>
      </div>
    </div>

    <AppSlotView
      :slot="apps.channelEditorSlots.channel.editor"
      :context="{
        channelKey: channelKey,
        data: slotData,
        setField: setAppField,
        removeField: removeAppField,
      }"
    />

    <div class="channel-editor__actions" v-if="canWrite">
      <button class="btn-primary" :disabled="!isDirty || saving" @click="save">
        {{ t('editor.save') }}
      </button>
      <button :disabled="!isDirty" @click="resetForm">
        {{ t('editor.reset') }}
      </button>
      <button class="btn-danger" @click="deleteChannel">
        {{ t('editor.delete') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.channel-editor {
  padding: 20px;
  max-width: 700px;
  overflow-y: auto;
  height: 100%;
}

.channel-editor h2 {
  font-family: monospace;
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0 0 20px 0;
}

.channel-editor__section {
  margin-bottom: 10px;
}

.countries-list {
  border: 1px solid hsl(var(--color-70));
  border-radius: 4px;
  margin-bottom: 8px;
}

.add-country {
  margin-top: 8px;
}

.add-country-search {
  padding: 4px;
}

.add-country-list {
  max-height: 300px;
  overflow-y: auto;
}

.channel-editor__actions {
  display: flex;
  gap: 8px;
  margin-top: 24px;
  padding-bottom: 20px;
}

.channel-editor__actions .btn-danger {
  margin-left: auto;
}
</style>
